import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../utils/api';
import { Sparkles, Zap, EyeOff, Eye, Trash2, Edit2, X, CheckCircle, XCircle, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

const STATUSES = ['active', 'pending_review', 'rejected', 'hidden', 'deleted', 'sold'];

// Preset rejection reasons — one-tap fill for polite owner message + internal reason.
const REJECTION_PRESETS = {
  prohibited: {
    label: 'Prohibited / Restricted item',
    reject: true,
    message: "Hi, thank you for using GenzoX. After reviewing your listing, we're unable to approve it because the product does not fall within the types of listings permitted on GenzoX under our Listing Guidelines. We therefore cannot publish this advertisement on our platform. We appreciate your understanding and cooperation.",
    reason: 'Product falls under a prohibited/restricted listing category under GenzoX Listing Guidelines.',
  },
  misleading: {
    label: 'Misleading / Inaccurate info',
    reject: true,
    message: "Hi, thank you for using GenzoX. After reviewing your listing, we're unable to approve it as it appears to contain misleading, inaccurate or deceptive information. Please review your title, description, price and images so they accurately represent the item, and you're welcome to edit and resubmit. We appreciate your understanding.",
    reason: 'Listing contains misleading, inaccurate or deceptive information.',
  },
  duplicate: {
    label: 'Duplicate listing',
    reject: true,
    message: "Hi, thank you for using GenzoX. We noticed this listing appears to be a duplicate of another advertisement. To keep the marketplace clear for buyers we allow only one active listing per item, so we're unable to publish this duplicate. We appreciate your understanding.",
    reason: 'Duplicate of an existing listing.',
  },
  wrong_category: {
    label: 'Wrong category / sub-category',
    reject: true,
    message: "Hi, thank you for using GenzoX. After reviewing your listing, we found the selected category or sub-category doesn't match the item. Please edit the listing and choose the most appropriate category so it reaches the right buyers, then resubmit. We appreciate your understanding.",
    reason: 'Listing placed in an incorrect category/sub-category.',
  },
};

const AdminListingsTab = () => {
  const [listings, setListings] = useState([]);
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('newest');
  const [selected, setSelected] = useState([]);
  const [editing, setEditing] = useState(null);
  const [responding, setResponding] = useState(null); // {listing, message, reason, reject}
  const [sendingResponse, setSendingResponse] = useState(false);

  const load = async () => {
    try { const res = await adminAPI.getListings(); setListings(res.data); }
    catch (e) { toast.error('Failed to load listings'); }
  };
  useEffect(() => { load(); }, []);

  const filtered = listings.filter((l) => {
    if (filter !== 'all' && l.status !== filter) return false;
    if (q && !`${l.title} ${l.listing_id} ${l.category_name || ''}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    if (sort === 'oldest') return String(a.created_at || '').localeCompare(String(b.created_at || ''));
    if (sort === 'price_high') return (b.price || 0) - (a.price || 0);
    if (sort === 'price_low') return (a.price || 0) - (b.price || 0);
    return String(b.created_at || '').localeCompare(String(a.created_at || '')); // newest (default)
  });

  const allVisibleSelected = sorted.length > 0 && sorted.slice(0, 200).every((l) => selected.includes(l.listing_id));
  const toggleSelectAll = () => {
    const visible = sorted.slice(0, 200).map((l) => l.listing_id);
    setSelected(allVisibleSelected ? selected.filter((id) => !visible.includes(id)) : [...new Set([...selected, ...visible])]);
  };
  const toggleSelect = (id) => setSelected(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);

  const approveListing = async (id) => {
    try { await adminAPI.approveListing(id); toast.success('Approved — now live'); load(); }
    catch (e) { toast.error('Failed to approve'); }
  };
  const rejectListing = async (id) => {
    const reason = window.prompt('Rejection reason (shown to owner):', 'Not eligible for listing on GenzoX');
    if (reason === null) return;
    try { await adminAPI.rejectListing(id, reason || 'Policy violation'); toast.success('Rejected'); load(); }
    catch (e) { toast.error('Failed to reject'); }
  };
  const submitResponse = async () => {
    if (!responding?.message?.trim()) { toast.error('Message required'); return; }
    setSendingResponse(true);
    try {
      const res = await adminAPI.respondListing(responding.listing.listing_id, {
        message: responding.message.trim(),
        reason: responding.reason?.trim() || '',
        reject: !!responding.reject,
      });
      toast.success(res.data?.emailed ? 'Response emailed to owner' : 'Response recorded');
      setResponding(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to send'); }
    finally { setSendingResponse(false); }
  };

  const forcePro = async (id) => {
    const d = parseInt(window.prompt('Pro duration in days?', '30'), 10);
    if (!d || d < 1) return;
    try { await adminAPI.forcePro(id, d); toast.success('Pro granted'); load(); }
    catch (e) { toast.error('Failed'); }
  };
  const forceBoost = async (id) => {
    const d = parseInt(window.prompt('Boost duration in days?', '7'), 10);
    if (!d || d < 1) return;
    try { await adminAPI.forceBoost(id, d); toast.success('Boosted'); load(); }
    catch (e) { toast.error('Failed'); }
  };
  const toggleHide = async (l) => {
    const hide = l.status !== 'hidden';
    try { await adminAPI.hideListing(l.listing_id, hide); toast.success(hide ? 'Hidden' : 'Unhidden'); load(); }
    catch (e) { toast.error('Failed'); }
  };
  const deleteListing = async (id) => {
    if (!window.confirm('Delete this listing?')) return;
    try { await adminAPI.deleteListing(id); toast.success('Deleted'); load(); }
    catch (e) { toast.error('Failed'); }
  };
  const openEdit = (l) => setEditing({
    listing_id: l.listing_id,
    title: l.title || '',
    description: l.description || '',
    price: l.price ?? 0,
    city: l.city || '',
    state: l.state || '',
    pincode: l.pincode || '',
    status: l.status || 'active',
  });
  const submitEdit = async () => {
    if (!editing.title.trim()) { toast.error('Title required'); return; }
    try {
      const fields = { ...editing };
      delete fields.listing_id;
      fields.price = parseFloat(fields.price) || 0;
      await adminAPI.updateListing(editing.listing_id, fields);
      toast.success('Listing updated');
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const bulkDelete = async (hard) => {
    if (selected.length === 0) return;
    const label = hard ? 'PERMANENTLY DELETE (database)' : 'soft-delete (status=deleted)';
    const typed = window.prompt(`Type DELETE to ${label} ${selected.length} listing${selected.length === 1 ? '' : 's'}:`, '');
    if (typed !== 'DELETE') { toast.error('Cancelled'); return; }
    try {
      const res = await adminAPI.bulkDelete(selected, hard);
      toast.success(`${res.data.deleted} listing${res.data.deleted === 1 ? '' : 's'} ${hard ? 'permanently deleted' : 'deleted'}`);
      setSelected([]);
      load();
    } catch (e) { toast.error('Failed'); }
  };

  return (
    <div className="space-y-3" data-testid="admin-listings-tab">
      <div className="bg-white rounded-xl p-3 border border-gray-100 flex flex-col sm:flex-row gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by title or id..."
          data-testid="listings-search" className="flex-1 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm outline-none focus:border-[#1800ad]" />
        <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="listings-filter"
          className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
          <option value="all">All ({listings.length})</option>
          <option value="active">Active</option>
          <option value="pending_review">Pending</option>
          <option value="rejected">Rejected</option>
          <option value="hidden">Hidden</option>
          <option value="deleted">Deleted</option>
          <option value="sold">Sold</option>
        </select>
        <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="listings-sort"
          className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
          <option value="price_high">Price: High → Low</option>
          <option value="price_low">Price: Low → High</option>
        </select>
      </div>

      {selected.length > 0 && (
        <div className="bg-[#fff4ed] border border-[#ff8500]/30 rounded-xl p-3 flex flex-col sm:flex-row sm:items-center gap-2" data-testid="bulk-actions-bar">
          <p className="text-sm font-bold text-[#0A0B10]">{selected.length} selected</p>
          <div className="flex flex-wrap gap-2 sm:ml-auto">
            <button onClick={() => setSelected([])} className="bg-white border border-gray-200 text-[#4B5563] rounded-lg px-3 py-1.5 text-xs font-bold" data-testid="bulk-clear">Clear</button>
            <button onClick={() => bulkDelete(false)} className="bg-red-50 text-red-600 hover:bg-red-600 hover:text-white border border-red-200 rounded-lg px-3 py-1.5 text-xs font-bold" data-testid="bulk-soft-delete">
              <Trash2 size={12} className="inline mr-1" /> Delete (soft)
            </button>
            <button onClick={() => bulkDelete(true)} className="bg-red-600 hover:bg-red-700 text-white rounded-lg px-3 py-1.5 text-xs font-bold" data-testid="bulk-hard-delete">
              <Trash2 size={12} className="inline mr-1" /> Delete permanently
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-100 overflow-x-auto">
        <table className="w-full text-xs sm:text-sm">
          <thead className="bg-[#f8f9fa] text-left">
            <tr>
              <th className="px-3 py-2 w-8">
                <input type="checkbox" checked={allVisibleSelected} onChange={toggleSelectAll} data-testid="listings-select-all" aria-label="Select all" />
              </th>
              <th className="px-3 py-2">Listing</th>
              <th className="px-3 py-2">Category</th>
              <th className="px-3 py-2 hidden sm:table-cell">Price</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sorted.slice(0, 200).map((l) => (
              <tr key={l.listing_id} className={`border-t border-gray-100 hover:bg-[#fafafa] ${selected.includes(l.listing_id) ? 'bg-[#fff4ed]/60' : ''}`} data-testid={`listing-row-${l.listing_id}`}>
                <td className="px-3 py-2">
                  <input type="checkbox" checked={selected.includes(l.listing_id)} onChange={() => toggleSelect(l.listing_id)} data-testid={`select-${l.listing_id}`} aria-label={`Select ${l.title}`} />
                </td>
                <td className="px-3 py-2 cursor-pointer group"
                    onClick={() => window.open(`/listing/${l.listing_id}`, '_blank', 'noopener')}
                    title="Open listing details"
                    data-testid={`open-listing-${l.listing_id}`}>
                  <p className="font-bold text-[#0A0B10] group-hover:text-[#1800ad] group-hover:underline truncate max-w-[220px]">{l.title}</p>
                  <p className="text-[10px] text-[#6B7280]">{l.listing_id} · {l.city || '—'}</p>
                </td>
                <td className="px-3 py-2" data-testid={`listing-category-${l.listing_id}`}>
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${l.category_available === false ? 'bg-red-50 text-red-600' : 'bg-[#eef2ff] text-[#334155]'}`}>
                    {l.category_name || '—'}
                  </span>
                  {l.subcategory_name && <p className="text-[10px] text-[#9CA3AF] mt-0.5">{l.subcategory_name}</p>}
                  {l.category_available === false && <p className="text-[9px] text-red-600 font-bold mt-0.5">not on GenzoX</p>}
                </td>
                <td className="px-3 py-2 hidden sm:table-cell font-bold text-[#1800ad]">₹{l.price?.toLocaleString()}</td>
                <td className="px-3 py-2">
                  <div className="flex gap-1 flex-wrap">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                      l.status === 'active' ? 'bg-green-50 text-green-600' :
                      l.status === 'pending_review' ? 'bg-amber-50 text-amber-700' :
                      l.status === 'rejected' ? 'bg-red-50 text-red-600' :
                      'bg-gray-100 text-gray-600'}`}>{(l.status || '').toUpperCase()}</span>
                    {l.is_pro && <span className="px-1.5 py-0.5 rounded bg-[#f0edff] text-[#1800ad] text-[10px] font-bold">PRO</span>}
                    {l.is_boosted && <span className="px-1.5 py-0.5 rounded bg-[#fff4ed] text-[#ff8500] text-[10px] font-bold">BOOST</span>}
                  </div>
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="inline-flex gap-1 flex-wrap justify-end">
                    {l.status === 'pending_review' && (
                      <>
                        <button onClick={() => approveListing(l.listing_id)} title="Approve (go live)" data-testid={`approve-listing-${l.listing_id}`}
                          className="p-1.5 rounded bg-green-50 text-green-600 hover:bg-green-600 hover:text-white"><CheckCircle size={13} /></button>
                        <button onClick={() => rejectListing(l.listing_id)} title="Reject" data-testid={`reject-listing-${l.listing_id}`}
                          className="p-1.5 rounded bg-red-50 text-red-600 hover:bg-red-600 hover:text-white"><XCircle size={13} /></button>
                      </>
                    )}
                    <button onClick={() => setResponding({ listing: l, message: '', reason: '', reject: false })} title="Respond to owner" data-testid={`respond-listing-${l.listing_id}`}
                      className="p-1.5 rounded bg-[#eef2ff] text-[#1800ad] hover:bg-[#1800ad] hover:text-white"><MessageSquare size={13} /></button>
                    <button onClick={() => openEdit(l)} title="Edit listing" data-testid={`edit-listing-${l.listing_id}`}
                      className="p-1.5 rounded bg-[#f0edff] text-[#1800ad] hover:bg-[#1800ad] hover:text-white"><Edit2 size={13} /></button>
                    <button onClick={() => forcePro(l.listing_id)} title="Force Pro" data-testid={`force-pro-${l.listing_id}`}
                      className="p-1.5 rounded bg-[#f0edff] text-[#1800ad] hover:bg-[#1800ad] hover:text-white"><Sparkles size={13} /></button>
                    <button onClick={() => forceBoost(l.listing_id)} title="Force Boost" data-testid={`force-boost-${l.listing_id}`}
                      className="p-1.5 rounded bg-[#fff4ed] text-[#ff8500] hover:bg-[#ff8500] hover:text-white"><Zap size={13} /></button>
                    <button onClick={() => toggleHide(l)} title={l.status === 'hidden' ? 'Unhide' : 'Hide'} data-testid={`hide-${l.listing_id}`}
                      className="p-1.5 rounded bg-gray-100 text-gray-700 hover:bg-gray-700 hover:text-white">
                      {l.status === 'hidden' ? <Eye size={13} /> : <EyeOff size={13} />}
                    </button>
                    <button onClick={() => deleteListing(l.listing_id)} title="Delete" data-testid={`delete-${l.listing_id}`}
                      className="p-1.5 rounded bg-red-50 text-red-600 hover:bg-red-600 hover:text-white"><Trash2 size={13} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {sorted.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-[#9CA3AF] text-sm">No listings match</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Edit listing modal */}
      {editing && (
        <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="edit-listing-modal">
            <div className="sticky top-0 bg-white border-b border-gray-100 p-4 flex items-center justify-between">
              <h3 className="font-bold text-[#0A0B10]">Edit listing</h3>
              <button onClick={() => setEditing(null)} className="p-1.5 rounded bg-gray-100"><X size={14} /></button>
            </div>
            <div className="p-4 space-y-3 text-sm">
              <div>
                <label className="text-xs font-bold text-[#4B5563]">Title</label>
                <input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                  data-testid="edit-listing-title" className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 mt-1 outline-none focus:border-[#1800ad]" />
              </div>
              <div>
                <label className="text-xs font-bold text-[#4B5563]">Description</label>
                <textarea rows={4} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  data-testid="edit-listing-desc" className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 mt-1 outline-none focus:border-[#1800ad]" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-bold text-[#4B5563]">Price (₹)</label>
                  <input type="number" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })}
                    data-testid="edit-listing-price" className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 mt-1 outline-none focus:border-[#1800ad]" />
                </div>
                <div>
                  <label className="text-xs font-bold text-[#4B5563]">Status</label>
                  <select value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })}
                    data-testid="edit-listing-status" className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 mt-1 outline-none focus:border-[#1800ad]">
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-xs font-bold text-[#4B5563]">City</label>
                  <input value={editing.city} onChange={(e) => setEditing({ ...editing, city: e.target.value })}
                    data-testid="edit-listing-city" className="w-full rounded-lg border border-gray-200 bg-gray-50 px-2 py-2 mt-1 outline-none focus:border-[#1800ad]" />
                </div>
                <div>
                  <label className="text-xs font-bold text-[#4B5563]">State</label>
                  <input value={editing.state} onChange={(e) => setEditing({ ...editing, state: e.target.value })}
                    data-testid="edit-listing-state" className="w-full rounded-lg border border-gray-200 bg-gray-50 px-2 py-2 mt-1 outline-none focus:border-[#1800ad]" />
                </div>
                <div>
                  <label className="text-xs font-bold text-[#4B5563]">PIN</label>
                  <input value={editing.pincode} onChange={(e) => setEditing({ ...editing, pincode: e.target.value })}
                    data-testid="edit-listing-pincode" className="w-full rounded-lg border border-gray-200 bg-gray-50 px-2 py-2 mt-1 outline-none focus:border-[#1800ad]" />
                </div>
              </div>
              <div className="flex gap-2 pt-1">
                <button onClick={submitEdit} data-testid="edit-listing-save" className="flex-1 bg-[#1800ad] hover:bg-[#1200a0] text-white rounded-lg px-4 py-2 font-bold">Save</button>
                <button onClick={() => setEditing(null)} className="bg-gray-200 text-[#4B5563] rounded-lg px-4 py-2 font-bold">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Respond to ad owner modal */}
      {responding && (
        <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4" onClick={() => !sendingResponse && setResponding(null)}>
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="respond-listing-modal">
            <div className="sticky top-0 bg-white border-b border-gray-100 p-4 flex items-center justify-between">
              <h3 className="font-bold text-[#0A0B10]">Respond to ad owner</h3>
              <button onClick={() => !sendingResponse && setResponding(null)} className="p-1.5 rounded bg-gray-100"><X size={14} /></button>
            </div>
            <div className="p-4 space-y-3 text-sm">
              <div className="bg-[#f8f9fa] rounded-lg p-2 text-xs">
                <p className="font-bold text-[#0A0B10] truncate">{responding.listing.title}</p>
                <p className="text-[#6B7280]">{responding.listing.category_name} · {responding.listing.listing_id}</p>
              </div>
              {responding.listing.admin_review?.message && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-xs" data-testid="existing-admin-review">
                  <p className="font-bold text-amber-800">Previous response</p>
                  <p className="text-amber-900 whitespace-pre-wrap">{responding.listing.admin_review.message}</p>
                </div>
              )}
              <div>
                <label className="text-xs font-bold text-[#4B5563]">Quick preset reason</label>
                <select
                  value={responding.preset || ''}
                  onChange={(e) => {
                    const key = e.target.value;
                    const p = REJECTION_PRESETS[key];
                    if (p) setResponding({ ...responding, preset: key, message: p.message, reason: p.reason, reject: p.reject });
                    else setResponding({ ...responding, preset: '' });
                  }}
                  data-testid="respond-preset-select"
                  className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 mt-1 text-sm outline-none focus:border-[#1800ad]">
                  <option value="">Custom message…</option>
                  {Object.entries(REJECTION_PRESETS).map(([k, p]) => (
                    <option key={k} value={k}>{p.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold text-[#4B5563]">Message to owner</label>
                <textarea rows={5} value={responding.message} onChange={(e) => setResponding({ ...responding, message: e.target.value })}
                  placeholder="Explain why the ad is not eligible, or ask for clarification…"
                  data-testid="respond-message" className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 mt-1 outline-none focus:border-[#1800ad]" />
              </div>
              <div>
                <label className="text-xs font-bold text-[#4B5563]">Reason (optional, saved to review record)</label>
                <input value={responding.reason} onChange={(e) => setResponding({ ...responding, reason: e.target.value })}
                  data-testid="respond-reason" className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 mt-1 outline-none focus:border-[#1800ad]" />
              </div>
              <label className="flex items-center gap-2 text-xs font-bold text-[#4B5563]">
                <input type="checkbox" checked={responding.reject} onChange={(e) => setResponding({ ...responding, reject: e.target.checked })} data-testid="respond-reject-toggle" />
                Reject this listing (mark not live)
              </label>
              <div className="flex gap-2 pt-1">
                <button onClick={submitResponse} disabled={sendingResponse} data-testid="respond-send" className="flex-1 bg-[#1800ad] hover:bg-[#1200a0] text-white rounded-lg px-4 py-2 font-bold disabled:opacity-60">
                  {sendingResponse ? 'Sending…' : (responding.reject ? 'Send & Reject' : 'Send response')}
                </button>
                <button onClick={() => setResponding(null)} disabled={sendingResponse} className="bg-gray-200 text-[#4B5563] rounded-lg px-4 py-2 font-bold">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminListingsTab;
